import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Smartphone,
  Wifi,
  Bluetooth,
  Users,
  Eye,
  RefreshCw,
  Download,
  Clock,
  MapPin,
  Signal,
  TrendingUp,
  BarChart3,
  Upload,
  Radio,
  Target,
  Play,
  PieChart as PieChartIcon,
  Lock,
  Settings,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { format, subDays } from 'date-fns';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function DeviceCapturePage() {
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [captures, setCaptures] = useState([]);
  const [exposureReport, setExposureReport] = useState(null);
  const [attributionReport, setAttributionReport] = useState(null);
  const [playLogs, setPlayLogs] = useState([]);
  const [screens, setScreens] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [content, setContent] = useState([]);
  
  // Filters
  const [screenFilter, setScreenFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // Manual capture dialog
  const [showCaptureDialog, setShowCaptureDialog] = useState(false);
  const [captureForm, setCaptureForm] = useState({
    device_id: '',
    device_type: 'mobile_id',
    latitude: '',
    longitude: '',
    timestamp: '',
  });
  
  // Play log dialog
  const [showPlayLogDialog, setShowPlayLogDialog] = useState(false);
  const [playLogForm, setPlayLogForm] = useState({
    screen_id: '',
    content_id: '',
    campaign_id: '',
    start_time: '',
    end_time: '',
    slot_duration_seconds: 10,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
        ...(screenFilter !== 'all' && { screen_id: screenFilter }),
        ...(campaignFilter !== 'all' && { campaign_id: campaignFilter }),
      };
      
      const [statsRes, capturesRes, exposureRes, screensRes, campaignsRes, contentRes] = await Promise.all([
        api.get('/devices/audience-stats', { params }),
        api.get('/devices/captures', { params: { ...params, limit: 50 } }),
        api.get('/devices/exposure-report', { params }),
        api.get('/screens'),
        api.get('/campaigns'),
        api.get('/content'),
      ]);
      
      setStats(statsRes.data);
      setCaptures(capturesRes.data.captures || []);
      setExposureReport(exposureRes.data);
      setScreens(screensRes.data);
      setCampaigns(campaignsRes.data);
      setContent(contentRes.data);
      
      // Load attribution report
      try {
        const attrRes = await api.get('/attribution/report', { params: { ...params, include_lotame: true } });
        setAttributionReport(attrRes.data);
      } catch (e) {
        console.log('Attribution report not available');
      }
      
      // Load play logs
      try {
        const playRes = await api.get('/play-logs', { params });
        setPlayLogs(playRes.data.play_logs || []);
      } catch (e) {
        console.log('Play logs not available');
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManualCapture = async () => {
    if (!captureForm.device_id || (!captureForm.latitude && !captureForm.longitude)) {
      toast.error('Device ID and coordinates are required');
      return;
    }
    
    try {
      const payload = {
        device_id: captureForm.device_id,
        device_type: captureForm.device_type,
        latitude: parseFloat(captureForm.latitude),
        longitude: parseFloat(captureForm.longitude),
      };
      if (captureForm.timestamp) {
        payload.timestamp = captureForm.timestamp;
      }
      
      const res = await api.post('/devices/capture', payload);
      toast.success(`Device captured! Matched to: ${res.data.screen_name} (${Math.round(res.data.distance_meters)}m away)`);
      setShowCaptureDialog(false);
      setCaptureForm({
        device_id: '',
        device_type: 'mobile_id',
        latitude: '',
        longitude: '',
        timestamp: '',
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to capture device');
    }
  };

  const handleCreatePlayLog = async () => {
    if (!playLogForm.screen_id || !playLogForm.content_id || !playLogForm.start_time || !playLogForm.end_time) {
      toast.error('Screen, Content, and times are required');
      return;
    }
    
    try {
      // Convert "none" to empty string for campaign_id
      const payload = {
        ...playLogForm,
        campaign_id: playLogForm.campaign_id === 'none' ? '' : playLogForm.campaign_id
      };
      await api.post('/play-logs', payload);
      toast.success('Play log created - Attribution matching started');
      setShowPlayLogDialog(false);
      setPlayLogForm({
        screen_id: '',
        content_id: '',
        campaign_id: '',
        start_time: '',
        end_time: '',
        slot_duration_seconds: 10,
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create play log');
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
        format: 'csv',
        ...(screenFilter !== 'all' && { screen_id: screenFilter }),
      };
      
      const response = await api.get('/devices/export', { params });
      
      // Download CSV
      const blob = new Blob([response.data.content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `device_ids_${startDate}_${endDate}.csv`;
      a.click();
      
      toast.success(`Exported ${response.data.total_devices} device IDs`);
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const getDeviceTypeIcon = (type) => {
    switch (type) {
      case 'wifi': return <Wifi className="w-4 h-4" />;
      case 'bluetooth': return <Bluetooth className="w-4 h-4" />;
      case 'mobile_id': return <Smartphone className="w-4 h-4" />;
      default: return <Radio className="w-4 h-4" />;
    }
  };

  const pieData = stats?.devices_by_type 
    ? Object.entries(stats.devices_by_type).map(([name, value]) => ({ name, value }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="device-capture-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            DOOH Attribution & Audience
          </h1>
          <p className="text-zinc-400 mt-1">
            Track devices, match with ad plays, and analyze audience segments
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowCaptureDialog(true)}
            className="border-zinc-700"
            data-testid="manual-capture-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Capture Device
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowPlayLogDialog(true)}
            className="border-zinc-700"
            data-testid="create-play-log-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Log Ad Play
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              onClick={handleExport}
              className="border-zinc-700"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export IDs
            </Button>
          )}
          <Button
            onClick={loadData}
            className="bg-blue-600 hover:bg-blue-500"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="glass-card border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Screen</Label>
              <Select value={screenFilter} onValueChange={setScreenFilter}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-48">
                  <SelectValue placeholder="All Screens" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Screens</SelectItem>
                  {screens.map((screen) => (
                    <SelectItem key={screen.id} value={screen.id}>{screen.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Campaign</Label>
              <Select value={campaignFilter} onValueChange={setCampaignFilter}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-48">
                  <SelectValue placeholder="All Campaigns" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Campaigns</SelectItem>
                  {campaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>{campaign.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white w-40"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white w-40"
              />
            </div>
            <Button onClick={loadData} className="bg-blue-600 hover:bg-blue-500">
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Unique Devices</p>
                  <p className="text-white text-2xl font-bold">{stats.total_unique_devices.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <Signal className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Total Detections</p>
                  <p className="text-white text-2xl font-bold">{stats.total_detections.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Target className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Attributed</p>
                  <p className="text-white text-2xl font-bold">{attributionReport?.attributed_devices || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Attribution Rate</p>
                  <p className="text-white text-2xl font-bold">
                    {attributionReport?.attribution_rate?.toFixed(1) || 0}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-pink-500/10">
                  <Play className="w-5 h-5 text-pink-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Ad Plays</p>
                  <p className="text-white text-2xl font-bold">{attributionReport?.total_ad_plays || playLogs.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="attribution" className="data-[state=active]:bg-blue-600">
            <Target className="w-4 h-4 mr-2" />
            Attribution
          </TabsTrigger>
          <TabsTrigger value="lotame" className="data-[state=active]:bg-blue-600">
            <PieChartIcon className="w-4 h-4 mr-2" />
            Audience Segments
          </TabsTrigger>
          <TabsTrigger value="captures" className="data-[state=active]:bg-blue-600">
            <Signal className="w-4 h-4 mr-2" />
            Captures ({captures.length})
          </TabsTrigger>
          <TabsTrigger value="playlogs" className="data-[state=active]:bg-blue-600">
            <Play className="w-4 h-4 mr-2" />
            Play Logs
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Hourly Distribution */}
            <Card className="glass-card border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-400" />
                  Hourly Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.hourly_distribution?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={stats.hourly_distribution}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="hour" stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
                      <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46' }}
                        labelFormatter={(value) => `${value}:00`}
                      />
                      <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-zinc-500">
                    No hourly data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Device Types */}
            <Card className="glass-card border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-emerald-400" />
                  Device Types
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-zinc-500">
                    No device type data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Devices by Screen */}
            <Card className="glass-card border-zinc-800 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-purple-400" />
                  Devices by Screen
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.devices_by_screen && Object.keys(stats.devices_by_screen).length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(stats.devices_by_screen).map(([screenId, count]) => {
                      const screen = screens.find(s => s.id === screenId);
                      return (
                        <div key={screenId} className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-white font-medium truncate">{screen?.name || screenId}</p>
                          <p className="text-2xl font-bold text-blue-400">{count.toLocaleString()}</p>
                          <p className="text-zinc-500 text-sm">detections</p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-zinc-500">
                    No screen data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Attribution Tab */}
        <TabsContent value="attribution" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Attribution by Campaign */}
            <Card className="glass-card border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-400" />
                  Attribution by Campaign
                </CardTitle>
              </CardHeader>
              <CardContent>
                {attributionReport?.by_campaign?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-zinc-500">Campaign</TableHead>
                        <TableHead className="text-zinc-500 text-right">Devices</TableHead>
                        <TableHead className="text-zinc-500 text-right">Attributions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attributionReport.by_campaign.map((item, idx) => (
                        <TableRow key={idx} className="border-zinc-800">
                          <TableCell className="text-white font-medium">{item.campaign_name}</TableCell>
                          <TableCell className="text-blue-400 text-right font-bold">{item.unique_devices}</TableCell>
                          <TableCell className="text-zinc-400 text-right">{item.total_attributions}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-zinc-500">
                    No attribution data yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Attribution by Screen */}
            <Card className="glass-card border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-emerald-400" />
                  Attribution by Screen
                </CardTitle>
              </CardHeader>
              <CardContent>
                {attributionReport?.by_screen?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-zinc-500">Screen</TableHead>
                        <TableHead className="text-zinc-500 text-right">Devices</TableHead>
                        <TableHead className="text-zinc-500 text-right">Attributions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attributionReport.by_screen.map((item, idx) => (
                        <TableRow key={idx} className="border-zinc-800">
                          <TableCell>
                            <div>
                              <p className="text-white font-medium">{item.screen_name}</p>
                              <p className="text-zinc-500 text-xs">{item.location}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-emerald-400 text-right font-bold">{item.unique_devices}</TableCell>
                          <TableCell className="text-zinc-400 text-right">{item.total_attributions}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-zinc-500">
                    No screen attribution data yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Privacy Notice */}
            {!isAdmin && (
              <Card className="glass-card border-zinc-800 lg:col-span-2 bg-zinc-900/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-amber-500/10">
                      <Lock className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Privacy Protected View</p>
                      <p className="text-zinc-400 text-sm mt-1">
                        You are viewing aggregated counts only. Individual device IDs are visible to administrators only for privacy compliance.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Admin: Device ID List */}
            {isAdmin && attributionReport?.device_ids_hashed?.length > 0 && (
              <Card className="glass-card border-zinc-800 lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                      <Lock className="w-5 h-5 text-amber-400" />
                      Device IDs (Admin Only)
                    </CardTitle>
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                      {attributionReport.device_ids_hashed.length} devices
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="max-h-48 overflow-y-auto bg-zinc-950 rounded-lg p-3">
                    <pre className="text-xs text-zinc-400 font-mono">
                      {attributionReport.device_ids_hashed.slice(0, 20).join('\n')}
                      {attributionReport.device_ids_hashed.length > 20 && `\n... and ${attributionReport.device_ids_hashed.length - 20} more`}
                    </pre>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Lotame / Audience Segments Tab */}
        <TabsContent value="lotame" className="mt-4">
          {attributionReport?.lotame_segments ? (
            <div className="space-y-4">
              {/* Lotame Summary */}
              <Card className="glass-card border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-purple-500/10">
                        <PieChartIcon className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">{attributionReport.lotame_segments.provider}</h3>
                        <p className="text-zinc-400 text-sm">Audience Segmentation</p>
                      </div>
                    </div>
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                      {attributionReport.lotame_segments.match_rate}% Match Rate
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-white">{attributionReport.lotame_segments.total_devices_submitted}</p>
                      <p className="text-zinc-500 text-sm">Devices Submitted</p>
                    </div>
                    <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-400">{attributionReport.lotame_segments.total_devices_matched}</p>
                      <p className="text-zinc-500 text-sm">Devices Matched</p>
                    </div>
                    <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-emerald-400">{attributionReport.lotame_segments.match_rate}%</p>
                      <p className="text-zinc-500 text-sm">Match Rate</p>
                    </div>
                  </div>
                  
                  <p className="text-amber-400 text-sm mt-4 p-3 bg-amber-500/5 rounded-lg">
                    {attributionReport.lotame_segments.note}
                  </p>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Demographics */}
                <Card className="glass-card border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-white">Demographics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <p className="text-zinc-400 text-sm mb-2">Age Groups</p>
                        {Object.entries(attributionReport.lotame_segments.demographics.age_groups).map(([age, pct]) => (
                          <div key={age} className="flex items-center justify-between mb-2">
                            <span className="text-zinc-300">{age}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-zinc-800 rounded-full overflow-hidden">
                                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-white font-medium w-12 text-right">{pct}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="pt-4 border-t border-zinc-800">
                        <p className="text-zinc-400 text-sm mb-2">Income Brackets</p>
                        {Object.entries(attributionReport.lotame_segments.demographics.income_brackets).map(([bracket, pct]) => (
                          <div key={bracket} className="flex items-center justify-between mb-2">
                            <span className="text-zinc-300">{bracket.replace(/_/g, ' ')}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-zinc-800 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-white font-medium w-12 text-right">{pct}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Interests */}
                <Card className="glass-card border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-white">Interests & Affinity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {attributionReport.lotame_segments.interests.map((interest, idx) => (
                      <div key={idx} className="flex items-center justify-between mb-3 p-3 bg-zinc-800/30 rounded-lg">
                        <span className="text-white">{interest.name}</span>
                        <div className="flex items-center gap-2">
                          <Badge 
                            className={
                              interest.affinity_index > 120 
                                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                : interest.affinity_index < 100
                                  ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                  : 'bg-zinc-700 text-zinc-300 border-zinc-600'
                            }
                          >
                            {interest.affinity_index > 100 ? '+' : ''}{interest.affinity_index - 100}
                          </Badge>
                          <span className="text-zinc-400 text-sm">Index: {interest.affinity_index}</span>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Segment Breakdown */}
              <Card className="glass-card border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-white">All Audience Segments</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-zinc-500">Segment</TableHead>
                        <TableHead className="text-zinc-500">Category</TableHead>
                        <TableHead className="text-zinc-500 text-right">Devices</TableHead>
                        <TableHead className="text-zinc-500 text-right">%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attributionReport.lotame_segments.segments.map((seg, idx) => (
                        <TableRow key={idx} className="border-zinc-800">
                          <TableCell className="text-white">{seg.segment_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-zinc-800 text-zinc-300 border-zinc-700 capitalize">
                              {seg.category}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-blue-400 text-right font-medium">
                            {seg.device_count.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-zinc-400 text-right">{seg.percentage}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="glass-card border-zinc-800">
              <CardContent className="p-12 text-center">
                <PieChartIcon className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No audience segment data available</p>
                <p className="text-zinc-500 text-sm mt-1">
                  Capture devices and create ad play logs to generate audience insights
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Captures Tab */}
        <TabsContent value="captures" className="mt-4">
          <Card className="glass-card border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white">
                Recent Device Captures
              </CardTitle>
            </CardHeader>
            <CardContent>
              {captures.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      {isAdmin && <TableHead className="text-zinc-500">Device ID (Hashed)</TableHead>}
                      <TableHead className="text-zinc-500">Type</TableHead>
                      <TableHead className="text-zinc-500">Screen</TableHead>
                      <TableHead className="text-zinc-500">Distance</TableHead>
                      <TableHead className="text-zinc-500">Content</TableHead>
                      <TableHead className="text-zinc-500">Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {captures.map((capture) => (
                      <TableRow key={capture.id} className="border-zinc-800">
                        {isAdmin && (
                          <TableCell className="font-mono text-xs text-zinc-400">
                            {capture.device_id_hashed?.substring(0, 16)}...
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge variant="outline" className="bg-zinc-800 text-zinc-300 border-zinc-700">
                            {getDeviceTypeIcon(capture.device_type)}
                            <span className="ml-1">{capture.device_type}</span>
                          </Badge>
                        </TableCell>
                        <TableCell className="text-white">{capture.screen_name}</TableCell>
                        <TableCell className="text-zinc-400">
                          {capture.distance_meters ? `${Math.round(capture.distance_meters)}m` : '-'}
                        </TableCell>
                        <TableCell>
                          {capture.content_playing ? (
                            <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                              {capture.content_playing.name}
                            </Badge>
                          ) : (
                            <span className="text-zinc-500">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-zinc-400">
                          {format(new Date(capture.timestamp), 'MMM d, HH:mm:ss')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <Signal className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">No device captures yet</p>
                  <p className="text-zinc-500 text-sm mt-1">
                    Use the API or manual capture to add devices
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Play Logs Tab */}
        <TabsContent value="playlogs" className="mt-4">
          <Card className="glass-card border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-white">
                  Ad Play Logs
                </CardTitle>
                <Button 
                  onClick={() => setShowPlayLogDialog(true)} 
                  className="bg-blue-600 hover:bg-blue-500"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Create Play Log
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {playLogs.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-500">Content</TableHead>
                      <TableHead className="text-zinc-500">Screen</TableHead>
                      <TableHead className="text-zinc-500">Campaign</TableHead>
                      <TableHead className="text-zinc-500">Time</TableHead>
                      <TableHead className="text-zinc-500 text-right">Attributed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {playLogs.map((log) => (
                      <TableRow key={log.id} className="border-zinc-800">
                        <TableCell className="text-white font-medium">{log.content_name}</TableCell>
                        <TableCell className="text-zinc-300">{log.screen_name}</TableCell>
                        <TableCell>
                          {log.campaign_name ? (
                            <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                              {log.campaign_name}
                            </Badge>
                          ) : (
                            <span className="text-zinc-500">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-zinc-400">
                          {format(new Date(log.start_time), 'MMM d, HH:mm')} - {format(new Date(log.end_time), 'HH:mm')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge className={
                            log.devices_attributed > 0 
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                              : 'bg-zinc-800 text-zinc-400 border-zinc-700'
                          }>
                            {log.devices_attributed} devices
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <Play className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">No play logs yet</p>
                  <p className="text-zinc-500 text-sm mt-1">
                    Create play logs to track when ads display on screens
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Manual Capture Dialog */}
      <Dialog open={showCaptureDialog} onOpenChange={setShowCaptureDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              Capture Device with Location
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Device ID</Label>
              <Input
                value={captureForm.device_id}
                onChange={(e) => setCaptureForm({ ...captureForm, device_id: e.target.value })}
                placeholder="IDFA / GAID / MAC Address"
                className="bg-zinc-800 border-zinc-700 text-white mt-1 font-mono"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Device Type</Label>
              <Select
                value={captureForm.device_type}
                onValueChange={(value) => setCaptureForm({ ...captureForm, device_type: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="mobile_id">Mobile ID (IDFA/GAID)</SelectItem>
                  <SelectItem value="wifi">WiFi</SelectItem>
                  <SelectItem value="bluetooth">Bluetooth</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Latitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={captureForm.latitude}
                  onChange={(e) => setCaptureForm({ ...captureForm, latitude: e.target.value })}
                  placeholder="40.7580"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Longitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={captureForm.longitude}
                  onChange={(e) => setCaptureForm({ ...captureForm, longitude: e.target.value })}
                  placeholder="-73.9855"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300">Timestamp (Optional)</Label>
              <Input
                type="datetime-local"
                value={captureForm.timestamp}
                onChange={(e) => setCaptureForm({ ...captureForm, timestamp: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            <p className="text-zinc-500 text-sm">
              Device will be auto-matched to the nearest screen within 500m radius
            </p>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCaptureDialog(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleManualCapture} className="bg-blue-600 hover:bg-blue-500">
              <MapPin className="w-4 h-4 mr-2" />
              Capture & Match
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Play Log Dialog */}
      <Dialog open={showPlayLogDialog} onOpenChange={setShowPlayLogDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              Log Ad Play
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Screen</Label>
              <Select
                value={playLogForm.screen_id}
                onValueChange={(value) => setPlayLogForm({ ...playLogForm, screen_id: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue placeholder="Select screen" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {screens.map((screen) => (
                    <SelectItem key={screen.id} value={screen.id}>{screen.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">Content</Label>
              <Select
                value={playLogForm.content_id}
                onValueChange={(value) => setPlayLogForm({ ...playLogForm, content_id: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue placeholder="Select content" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {content.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">Campaign (Optional)</Label>
              <Select
                value={playLogForm.campaign_id}
                onValueChange={(value) => setPlayLogForm({ ...playLogForm, campaign_id: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue placeholder="Select campaign" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="none">None</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Start Time</Label>
                <Input
                  type="datetime-local"
                  value={playLogForm.start_time ? playLogForm.start_time.slice(0, 16) : ''}
                  onChange={(e) => setPlayLogForm({ ...playLogForm, start_time: new Date(e.target.value).toISOString() })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-300">End Time</Label>
                <Input
                  type="datetime-local"
                  value={playLogForm.end_time ? playLogForm.end_time.slice(0, 16) : ''}
                  onChange={(e) => setPlayLogForm({ ...playLogForm, end_time: new Date(e.target.value).toISOString() })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-300">Slot Duration (seconds)</Label>
              <Input
                type="number"
                value={playLogForm.slot_duration_seconds}
                onChange={(e) => setPlayLogForm({ ...playLogForm, slot_duration_seconds: parseInt(e.target.value) })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowPlayLogDialog(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleCreatePlayLog} className="bg-blue-600 hover:bg-blue-500">
              <Play className="w-4 h-4 mr-2" />
              Create Play Log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Documentation Card */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white">
            API Integration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-zinc-400 mb-4">
            Send device captures with lat/long coordinates for automatic screen matching (500m radius):
          </p>
          <div className="bg-zinc-950 rounded-lg p-4 font-mono text-sm overflow-x-auto">
            <pre className="text-zinc-300">
{`# Single device capture with geo-matching
POST /api/devices/capture
{
  "device_id": "IDFA-12345-ABCDE",
  "device_type": "mobile_id",
  "latitude": 40.7580,
  "longitude": -73.9855,
  "timestamp": "2024-12-15T14:00:05Z"
}

# Batch upload with geo-matching
POST /api/devices/capture/batch
{
  "sensor_id": "sensor-001",
  "captures": [
    {"device_id": "IDFA-001", "latitude": 40.7580, "longitude": -73.9855},
    {"device_id": "IDFA-002", "latitude": 40.7582, "longitude": -73.9857}
  ]
}

# Log ad play for attribution
POST /api/play-logs
{
  "screen_id": "screen-uuid",
  "content_id": "content-uuid",
  "campaign_id": "campaign-uuid",
  "start_time": "2024-12-15T14:00:00Z",
  "end_time": "2024-12-15T14:00:10Z"
}

# Get attribution report
GET /api/attribution/report?include_lotame=true`}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
